import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { LogOut, Phone, MapPin, Mail, User, UtensilsCrossed, Table, Truck } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useTable } from '../../context/TableContext';
import { FirestoreService } from '../../firebase/firestore';
import { Customer } from '../../types';

const CustomerProfile: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const { user, logout } = useAuth();
  const { selectedTable } = useTable();
  const [customerData, setCustomerData] = useState<Customer | null>(null);
  const [customerSession, setCustomerSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const handleLogout = () => {
    // Check if it's a customer session or regular user
    const session = localStorage.getItem('bookfood_customer_session');
    if (session) {
      // Customer session logout
      localStorage.removeItem('bookfood_customer_session');
      localStorage.removeItem('bookfood_selectedTableId');
      setCurrentScreen('welcome');
    } else {
      // Regular user logout
      logout();
    }
  };

  // Refresh customer data when component becomes visible again
  const refreshCustomerData = useCallback(async () => {
    if (user?.id) {
      try {
        console.log('🔄 CustomerProfile: Refreshing customer data...');
        const customer = await FirestoreService.getCustomer(user.id);
        if (customer) {
          console.log('✅ CustomerProfile: Customer data refreshed:', JSON.stringify(customer, null, 2));
          setCustomerData(customer);
        }
      } catch (error) {
        console.error('❌ CustomerProfile: Error refreshing customer data:', error);
      }
    }
  }, [user?.id]);

  // Load customer session data or Firestore data
  useEffect(() => {
    const loadCustomerData = async () => {
      console.log('🔍 CustomerProfile: Loading customer data...');
      
      // First check for customer session
      const session = localStorage.getItem('bookfood_customer_session');
      if (session) {
        try {
          const sessionData = JSON.parse(session);
          console.log('✅ CustomerProfile: Customer session found:', sessionData);
          setCustomerSession(sessionData);
          setLoading(false);
          return;
        } catch (error) {
          console.error('❌ CustomerProfile: Error parsing customer session:', error);
        }
      }
      
      // Fallback to Firestore data for registered users
      if (user?.id) {
        try {
          console.log('🔍 CustomerProfile: Fetching customer data for Firebase UID:', user.id);
          const customer = await FirestoreService.getCustomer(user.id);
          
          if (customer) {
            console.log('✅ CustomerProfile: Customer data loaded from Firestore:', customer);
            setCustomerData(customer);
          } else {
            console.log('⚠️ CustomerProfile: No customer data found in Firestore');
          }
        } catch (error) {
          console.error('❌ CustomerProfile: Error fetching customer data:', error);
        }
      }
      
      setLoading(false);
    };

    loadCustomerData();
  }, [user]);

  // Refresh data when component becomes visible (e.g., returning from edit profile)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user?.id) {
        refreshCustomerData();
      }
    };

    // Also refresh when the window gains focus (for mobile)
    const handleFocus = () => {
      if (user?.id) {
        refreshCustomerData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user?.id, refreshCustomerData]);

  const profileOptions = [
    { id: 'logout', label: 'Logout', icon: LogOut, color: 'text-red-400' }
  ];

  const ProfileOption: React.FC<{ option: any; index: number }> = ({ option, index }) => {
    const handleClick = () => {
      if (option.id === 'logout') {
        handleLogout();
      }
    };

    return (
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.1 }}
        onClick={handleClick}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="w-full flex items-center space-x-4 p-5 food-card hover:shadow-xl transition-all duration-200 hover:border-orange-500"
      >
        <div className={`p-3 rounded-xl bg-slate-700 ${option.color}`}>
          <option.icon size={24} />
        </div>
        <span className="text-white font-semibold text-lg">{option.label}</span>
      </motion.button>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-lg bg-slate-900 bg-opacity-80 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                <UtensilsCrossed className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
                Profile
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Table Selection Banner */}
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="food-card"
        >
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-24 h-24 bg-gradient-to-r from-orange-500 to-red-500 rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg"
            >
              {customerData?.profilePicture || user?.profilePicture ? (
                <img
                  src={customerData?.profilePicture || user?.profilePicture}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                <span className="text-3xl font-bold text-white">
                  {(customerSession?.name || customerData?.name || user?.name || 'Guest').charAt(0).toUpperCase()}
                </span>
              )}
            </motion.div>
            <h2 className="text-2xl font-bold text-white mb-2">
              {customerSession?.name || customerData?.name || user?.name || 'Guest'}
            </h2>
            <p className="text-gray-400">
              {customerSession ? 'Guest Customer' : `Customer ID: ${customerData?.customerId || user?.customerId || 'N/A'}`}
            </p>
          </div>
        </motion.div>

        {/* Customer Details */}
        {loading ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="food-card"
          >
            <div className="text-center text-gray-400">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-2"></div>
              Loading profile details...
            </div>
          </motion.div>
        ) : customerSession || customerData ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="food-card"
          >
            <h3 className="text-xl font-bold text-white mb-6 flex items-center">
              <User className="mr-2 text-orange-400" size={24} />
              {customerSession ? 'Session Information' : 'Contact Information'}
            </h3>
            <div className="space-y-4">
              {/* Phone */}
              <div className="flex items-center space-x-4 bg-slate-700 rounded-xl p-4">
                <div className="p-3 rounded-xl bg-slate-600">
                  <Phone className="text-green-400" size={20} />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Phone</p>
                  <p className="text-white font-medium">
                    {customerSession?.phone || customerData?.phone || 'Not provided'}
                  </p>
                </div>
              </div>

              {/* Order Type */}
              {customerSession?.orderType && (
                <div className="flex items-center space-x-4 bg-slate-700 rounded-xl p-4">
                  <div className="p-3 rounded-xl bg-slate-600">
                    {customerSession.orderType === 'dine-in' ? (
                      <Table className="text-blue-400" size={20} />
                    ) : (
                      <Truck className="text-purple-400" size={20} />
                    )}
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Order Type</p>
                    <p className="text-white font-medium capitalize">
                      {customerSession.orderType.replace('-', ' ')}
                    </p>
                  </div>
                </div>
              )}

              {/* Table Information */}
              {selectedTable && customerSession?.orderType === 'dine-in' && (
                <div className="flex items-center space-x-4 bg-slate-700 rounded-xl p-4">
                  <div className="p-3 rounded-xl bg-slate-600">
                    <Table className="text-orange-400" size={20} />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Selected Table</p>
                    <p className="text-white font-medium">
                      Table {selectedTable.tableNumber}
                    </p>
                    <p className="text-gray-400 text-sm">
                      {selectedTable.location} • {selectedTable.capacity} seats
                    </p>
                  </div>
                </div>
              )}

              {/* Delivery Address */}
              {customerSession?.address && customerSession?.orderType === 'delivery' && (
                <div className="flex items-start space-x-4 bg-slate-700 rounded-xl p-4">
                  <div className="p-3 rounded-xl bg-slate-600 mt-1">
                    <MapPin className="text-red-400" size={20} />
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-400 text-sm">Delivery Address</p>
                    <p className="text-white font-medium">{customerSession.address}</p>
                  </div>
                </div>
              )}

              {/* Email (for registered users) */}
              {!customerSession && customerData?.email && (
                <div className="flex items-center space-x-4 bg-slate-700 rounded-xl p-4">
                  <div className="p-3 rounded-xl bg-slate-600">
                    <Mail className="text-blue-400" size={20} />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Email</p>
                    <p className="text-white font-medium">{customerData.email}</p>
                  </div>
                </div>
              )}

              {/* Address (for registered users) */}
              {!customerSession && customerData?.address && (
                <div className="flex items-start space-x-4 bg-slate-700 rounded-xl p-4">
                  <div className="p-3 rounded-xl bg-slate-600 mt-1">
                    <MapPin className="text-red-400" size={20} />
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-400 text-sm">Address</p>
                    <p className="text-white font-medium">{customerData.address}</p>
                    <div className="flex space-x-2 mt-1">
                      <span className="text-gray-400 text-sm">{customerData.city || 'City not provided'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Registration Date (for registered users) */}
              {!customerSession && customerData?.registrationDate && (
                <div className="flex items-center space-x-4 bg-slate-700 rounded-xl p-4 border-t border-gray-600">
                  <div className="p-3 rounded-xl bg-slate-600">
                    <User className="text-purple-400" size={20} />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Member Since</p>
                    <p className="text-white font-medium">
                      {new Date(customerData.registrationDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}

              {/* Session Info */}
              {customerSession && (
                <div className="flex items-center space-x-4 bg-slate-700 rounded-xl p-4 border-t border-gray-600">
                  <div className="p-3 rounded-xl bg-slate-600">
                    <User className="text-yellow-400" size={20} />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Session Type</p>
                    <p className="text-white font-medium">Guest Customer</p>
                    <p className="text-gray-400 text-xs">Temporary session - no account required</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="food-card"
          >
            <div className="text-center text-gray-400">
              <p>Profile details not available</p>
              <p className="text-sm mt-1">Please complete your profile setup</p>
            </div>
          </motion.div>
        )}

        {/* Account Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h3 className="text-xl font-bold text-white mb-4">Account Actions</h3>
          <div className="space-y-3">
            {profileOptions.map((option, index) => (
              <ProfileOption key={option.id} option={option} index={index} />
            ))}
          </div>
        </motion.div>

        {/* App Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center py-4"
        >
          <p className="text-gray-400 text-sm">FoodHub v1.0.0</p>
          <p className="text-gray-500 text-xs mt-1">Your Food Partner</p>
        </motion.div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default CustomerProfile;
